import { updateSalesLead } from '@/api/calls/directus-calls/sales-leads-calls'
import type { DirectusSalesLead } from '@/types/directus-types'
import generateUniqueLocalID from '@/utils/generateUniqueLocalID'
import Trip from '../trip_classes/trip'
import User from '../user_classes/user'
import Vehicle from '../vehicle_classes/vehicle'
import type { SalesLeadComparisonData, SalesLeadOptions } from './salesLeadTypes'

export default class SalesLead {
  // -------------------------------------------------------------------- //
  // ------------------------- Global class state ----------------------- //
  // -------------------------------------------------------------------- //

  // global record of class instance ids this session.
  static usedIds: string[] = []

  // -------------------------------------------------------------------- //
  // ------------------------------- State ------------------------------ //
  // -------------------------------------------------------------------- //

  /**
   * The trip associated with this sales lead.
   */
  trip?: Trip

  /**
   * The external id of the sales lead.
   */
  external_id?: string | number

  /**
   * The local id of the sales lead.
   */
  local_id: string

  /**
   * The user associated with this sales lead.
   */
  user: User

  /**
   * Has the sales lead been contacted?
   */
  contacted: boolean

  /**
   * The date the sales lead was contacted.
   */
  contactedDate?: Date

  /**
   * The user who contacted the sales lead.
   */
  contactedBy?: User

  /**
   * The recommended vehicles for the sales lead.
   */
  recommendedVehicles: Vehicle[]

  /**
   * The comparison data for the sales lead.
   */
  comparisonData?: SalesLeadComparisonData

  /**
   * The date the sales lead was created.
   */
  dateCreated?: Date

  // -------------------------------------------------------------------- //
  // --------------------------- Constructor ---------------------------- //
  // -------------------------------------------------------------------- //

  /**
   * Constructor for creating a new SalesLead object.
   *
   * @param {SalesLeadOptions} options - The options to initialize the SalesLead object.
   */
  constructor(options: SalesLeadOptions) {
    this.local_id = options.local_id ?? generateUniqueLocalID(SalesLead.usedIds, 'sales-lead')
    this.trip = options.trip
    this.external_id = options.external_id
    this.user = options.user
    this.contacted = options.contacted ?? false
    this.contactedDate = options.contactedDate
    this.contactedBy = options.contactedBy
    this.recommendedVehicles = options.recommendedVehicles
    this.comparisonData = options.comparisonData
    this.dateCreated = options.dateCreated
  }

  /**
   * Creates a new `SalesLead` class instance from the provided `DirectusSalesLead` data.
   *
   * @param {DirectusSalesLead} data - The directus data to create a `SalesLead` from.
   * @throws {Error} If the data is missing a user or if the user is not an object.
   * @returns {SalesLead} The new `SalesLead` class instance.
   */
  static fromDirectusData(data: DirectusSalesLead): SalesLead {
    if (!data.user) throw new Error('SalesLead data missing user')
    if (typeof data.user !== 'object') throw new Error('SalesLead data missing user details')

    return new SalesLead({
      external_id: data.id,
      user: User.fromDirectusData(data.user),
      contacted: data.contacted ?? false,
      contactedBy:
        data.contacted_by && typeof data.contacted_by === 'object'
          ? User.fromDirectusData(data.contacted_by)
          : undefined,
      contactedDate: data.contacted_on ? new Date(data.contacted_on) : undefined,
      comparisonData: data.comparison_data ?? undefined,
      recommendedVehicles: data.recommended_vehicles
        ? data.recommended_vehicles.map((vehicle) => Vehicle.fromDirectusData(vehicle))
        : [],
      trip: data.trip && typeof data.trip === 'object' ? Trip.fromSavedData(data.trip) : undefined,
      dateCreated: data.date_created ? new Date(data.date_created) : undefined,
    })
  }

  // -------------------------------------------------------------------- //
  // ---------------------------- Functions ----------------------------- //
  // -------------------------------------------------------------------- //

  /**
   * Sets the sales lead as contacted and saves the updated data to directus.
   *
   * @param {User} [currentUser] - The user who contacted the sales lead.
   * @returns {Promise<void>} - Resolves when the sales lead data has been updated in directus.
   */
  public async flagAsContacted(currentUser?: User) {
    if (currentUser) this.contactedBy = currentUser
    this.contacted = true
    this.contactedDate = new Date()
    await this.saveUpdatedData()
  }

  /**
   * Updates the sales lead in directus with the current sales lead data.
   *
   * @returns {Promise<void>} - Resolves when the sales lead data has been updated in directus.
   */
  public async saveUpdatedData() {
    if (!this.external_id) throw new Error("Can't update SalesLead has no external id")
    const compiledData: Partial<DirectusSalesLead> = {
      id: this.external_id,
      contacted: this.contacted,
      contacted_on: this.contactedDate?.toISOString(),
      contacted_by: this.contactedBy?.directusId,
      user: this.user.directusId,
      comparison_data: this.comparisonData,
      recommended_vehicles: this.recommendedVehicles
        .map((vehicle) => vehicle.directusId)
        .filter((id) => id !== undefined),
    }
    const res = await updateSalesLead(this.external_id, compiledData)
  }
}
